import { createContext, type PropsWithChildren, useCallback, useEffect, useState } from 'react';
import { useHistory, useLocation } from '@direct-frontend/modules-router';
import { useIsomorphicLayoutEffect } from '@direct-frontend/stdlib';
import { useAutomaticPresentationCoordinator } from './AutomaticPresentationProvider';

type PendingRouteTransition = Readonly<{
    generation: number;
    pathname: string;
}>;

/** Позволяет route-регистрациям обновить срок жизни, даже если их React-компонент пережил смену маршрута. */
export const AutomaticPresentationRouteGenerationContext = createContext<number | null>(null);

/**
 * Связывает смену страницы с coordinator. Новым маршрутом считается изменение pathname:
 * query-параметры, hash и history state сохраняют регистрации текущей страницы.
 *
 * Очередь открывается после отрисовки новой ветки маршрута или её Suspense fallback. Если важный
 * кандидат находится за Suspense, fallback должен зарегистрировать его как pending до загрузки данных.
 *
 * @kind component
 */
export function AutomaticPresentationRouteBoundary({ children }: PropsWithChildren) {
    const coordinator = useAutomaticPresentationCoordinator();
    const location = useLocation();
    const [latestTransition, setLatestTransition] = useState<PendingRouteTransition | null>(null);
    const beginRouteTransition = useCallback(
        (pathname: string) => {
            setLatestTransition({
                generation: coordinator.beginRouteTransition(),
                pathname,
            });
        },
        [coordinator]
    );
    const routeGeneration = latestTransition?.generation ?? coordinator.getSnapshot().route.generation;

    return (
        <AutomaticPresentationRouteGenerationContext.Provider value={routeGeneration}>
            <AutomaticPresentationRouteObserver
                latestTransition={latestTransition}
                pathname={location.pathname}
                onRouteTransition={beginRouteTransition}
            />
            {children}
        </AutomaticPresentationRouteGenerationContext.Provider>
    );
}

type AutomaticPresentationRouteObserverProps = Readonly<{
    latestTransition: PendingRouteTransition | null;
    pathname: string;
    onRouteTransition: (pathname: string) => void;
}>;

function AutomaticPresentationRouteObserver({
    latestTransition,
    pathname,
    onRouteTransition,
}: AutomaticPresentationRouteObserverProps) {
    const coordinator = useAutomaticPresentationCoordinator();
    const history = useHistory();

    useIsomorphicLayoutEffect(() => {
        let previousPathname = history.location.pathname;

        return history.listen(({ location: nextLocation }) => {
            if (nextLocation.pathname === previousPathname) {
                return;
            }

            previousPathname = nextLocation.pathname;
            onRouteTransition(nextLocation.pathname);
        });
    }, [history, onRouteTransition]);

    useEffect(() => {
        // При нескольких быстрых переходах открыть очередь может только последняя отрисованная страница.
        if (!latestTransition || latestTransition.pathname !== pathname) {
            return;
        }

        coordinator.completeRouteTransition(latestTransition.generation);
    }, [coordinator, latestTransition, pathname]);

    return null;
}
